import { useState, useEffect } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CircleNotch, Key, Sparkle, EnvelopeSimple, Bank, CheckCircle, Cube } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

function ClaimAccountCard() {
  const { user, refresh } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  if (!user?.wix_provisioned || user?.email_claimed) return null;
  const claim = async () => {
    if (!email.includes("@") || password.length < 6) { toast.error("Correo válido y contraseña de 6+ caracteres."); return; }
    setBusy(true);
    try {
      await api.post("/wix/dashboard/claim", { email: email.trim(), password });
      await refresh();
      toast.success("¡Listo! Ya puedes entrar en /login con este correo.");
      setPassword("");
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="border border-gold/30 rounded-xl p-6 bg-gold/[0.05] space-y-4 mb-6" data-testid="claim-account-card">
      <div className="flex items-center gap-2"><Key size={20} className="text-gold" /><h3 className="font-semibold">Reclama tu cuenta</h3></div>
      <p className="text-xs text-neutral-300">Tu tienda se creó desde Wix. Agrega un correo y contraseña para poder entrar también fuera de Wix (en la página /login).</p>
      <div className="grid sm:grid-cols-2 gap-3">
        <div><Label>Correo</Label><Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tucorreo@ejemplo.com" className="mt-1 bg-white/5 border-white/15" data-testid="claim-email" /></div>
        <div><Label>Contraseña (6+)</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 bg-white/5 border-white/15" data-testid="claim-password" /></div>
      </div>
      <Button onClick={claim} disabled={busy} className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="claim-submit">
        {busy ? <CircleNotch className="animate-spin" /> : "Guardar acceso"}
      </Button>
    </div>
  );
}

export default function Settings() {
  const { user, setUser } = useAuth();
  const { t } = useTranslation();
  const ai = user?.ai_settings || {};
  const es = user?.email_settings || {};
  const ms = user?.meshy || {};
  const [provider, setProvider] = useState(ai.provider === "emergent" ? "google" : (ai.provider || "google"));
  const [apiKey, setApiKey] = useState("");
  const [model, setModel] = useState(ai.model || "");
  const [saving, setSaving] = useState(false);
  const [gmail, setGmail] = useState(es.gmail_address || "");
  const [appPassword, setAppPassword] = useState("");
  const [savingEmail, setSavingEmail] = useState(false);
  const [connect, setConnect] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [meshyKey, setMeshyKey] = useState("");
  const [savingMeshy, setSavingMeshy] = useState(false);
  const td = user?.threedai || {};
  const [threedProvider, setThreedProvider] = useState(td.provider || "meshy");
  const [threedaiKey, setThreedaiKey] = useState("");
  const [savingThreed, setSavingThreed] = useState(false);
  const saveThreed = async () => {
    setSavingThreed(true);
    try {
      const payload = { provider: threedProvider };
      if (threedaiKey) payload.api_key = threedaiKey;
      const { data } = await api.put("/settings/threedai", payload);
      setUser(data); setThreedaiKey("");
      toast.success(t("settings.gen3dSaved"));
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingThreed(false); }
  };
  const [gmailConn, setGmailConn] = useState(null);

  const loadGmail = () => api.get("/integrations/gmail/status").then((r) => setGmailConn(r.data)).catch(() => {});
  useEffect(() => {
    loadGmail();
    const q = new URLSearchParams(window.location.search);
    if (q.get("gmail") === "connected") { toast.success(t("settings.gmailConnected")); window.history.replaceState({}, "", window.location.pathname); }
    if (q.get("gmail_error")) { toast.error(t("settings.gmailError", { err: q.get("gmail_error") })); window.history.replaceState({}, "", window.location.pathname); }
  }, []);

  const connectGmail = async () => {
    try { const { data } = await api.get("/integrations/gmail/oauth/start"); window.location.assign(data.authorization_url); }
    catch (e) { toast.error(apiError(e)); }
  };
  const disconnectGmail = async () => {
    if (!window.confirm(t("settings.confirmDisconnectGmail"))) return;
    try { await api.delete("/integrations/gmail/connection"); toast.success(t("settings.gmailDisconnected")); loadGmail(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const saveMeshy = async () => {
    setSavingMeshy(true);
    try {
      const { data } = await api.put("/settings/meshy", { api_key: meshyKey || null });
      setUser(data); setMeshyKey("");
      toast.success(t("settings.meshySaved"));
    } catch (e) { toast.error(apiError(e)); }
    finally { setSavingMeshy(false); }
  };

  useEffect(() => { api.get("/connect/status").then((r) => setConnect(r.data)).catch(() => {}); }, []);

  const onboard = async () => {
    setConnecting(true);
    try {
      const { data } = await api.post("/connect/onboard", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(apiError(e)); setConnecting(false); }
  };

  const saveEmail = async () => {
    setSavingEmail(true);
    try {
      const payload = { gmail_address: gmail };
      if (appPassword) payload.app_password = appPassword.replace(/\s/g, "");
      const { data } = await api.put("/settings/email", payload);
      setUser(data); setAppPassword("");
      toast.success(t("settings.emailSaved"));
    } catch (err) { toast.error(apiError(err)); }
    finally { setSavingEmail(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { provider, model: model || null };
      if (apiKey) payload.api_key = apiKey;
      const { data } = await api.put("/settings/ai", payload);
      setUser(data);
      setApiKey("");
      toast.success(t("settings.settingsSaved"));
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-8 max-w-3xl">
      <h1 className="text-3xl font-bold tracking-tight">{t("settings.title")}</h1>
      <p className="text-neutral-400 mt-1 mb-8">{t("settings.subtitle")}</p>

      <ClaimAccountCard />

      <div className="border border-white/10 rounded-xl p-6 bg-[#121212] space-y-5">
        <div className="flex items-center gap-2"><Key size={20} className="text-gold" /><h3 className="font-semibold">{t("settings.aiEngine")}</h3></div>
        <p className="text-xs text-neutral-400">{t("settings.aiEngineDesc")}</p>

        <div>
          <Label>{t("settings.provider")}</Label>
          <Select value={provider} onValueChange={setProvider}>
            <SelectTrigger className="mt-2 bg-white/5 border-white/15" data-testid="ai-provider-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#1a1a1a] border-white/15 text-neutral-200">
              <SelectItem value="google">{t("settings.providerGoogle")}</SelectItem>
              <SelectItem value="openai">{t("settings.providerOpenai")}</SelectItem>
              <SelectItem value="anthropic">{t("settings.providerAnthropic")}</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {provider === "anthropic" && (
          <div className="flex items-start gap-2 text-xs text-amber-300 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3" data-testid="anthropic-warning">
            <Sparkle size={16} className="shrink-0 mt-0.5" /> {t("settings.anthropicWarning")}
          </div>
        )}

        <div>
          <Label>{provider === "google" ? t("settings.keyGoogle") : provider === "openai" ? t("settings.keyOpenai") : t("settings.keyAnthropic")}</Label>
          <Input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
            placeholder={ai.api_key_masked || (provider === "google" ? "AIza..." : provider === "openai" ? "sk-..." : "sk-ant-...")}
            className="mt-2 bg-white/5 border-white/15 font-mono" data-testid="ai-key-input" />
          <p className="text-xs text-neutral-500 mt-2">
            {provider === "google" && t("settings.getKeyGoogle")}
            {provider === "openai" && t("settings.getKeyOpenai")}
            {provider === "anthropic" && t("settings.getKeyAnthropic")}
            {ai.api_key_masked && ` ${t("settings.current", { val: ai.api_key_masked })}`}
          </p>
        </div>

        <div>
          <Label>{t("settings.model")}</Label>
          <Input value={model} onChange={(e) => setModel(e.target.value)}
            placeholder={provider === "google" ? "gemini-2.5-flash" : provider === "openai" ? "gpt-4o" : "claude-sonnet-4-6"}
            className="mt-2 bg-white/5 border-white/15 font-mono" data-testid="ai-model-input" />
          <p className="text-xs text-neutral-500 mt-1">{t("settings.modelHint")}</p>
        </div>

        <Button onClick={save} disabled={saving} className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="save-settings-btn">
          {saving ? <CircleNotch className="animate-spin" /> : t("settings.saveSettings")}
        </Button>
      </div>

      <div className="border border-white/10 rounded-xl p-6 bg-[#121212] space-y-5 mt-6">
        <div className="flex items-center gap-2"><Cube size={20} className="text-gold" /><h3 className="font-semibold">{t("settings.gen3d")}</h3></div>
        <p className="text-xs text-neutral-400">{t("settings.gen3dDesc")}</p>
        <div>
          <Label>{t("settings.provider")}</Label>
          <Select value={threedProvider} onValueChange={setThreedProvider}>
            <SelectTrigger className="mt-2 bg-white/5 border-white/15" data-testid="threed-provider-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="3daistudio">{t("settings.provider3daistudio")}</SelectItem>
              <SelectItem value="meshy">{t("settings.providerMeshy")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {threedProvider === "3daistudio" ? (
          <div>
            <Label>3D AI Studio API Key</Label>
            <Input type="password" value={threedaiKey} onChange={(e) => setThreedaiKey(e.target.value)}
              placeholder={td.api_key_masked || "3D AI Studio key"} className="mt-2 bg-white/5 border-white/15 font-mono" data-testid="threedai-key-input" />
            <p className="text-xs text-neutral-500 mt-2">{t("settings.getKey3daistudio")}{td.api_key_masked && ` ${t("settings.current", { val: td.api_key_masked })}`}</p>
          </div>
        ) : (
          <div>
            <Label>Meshy API Key</Label>
            <Input type="password" value={meshyKey} onChange={(e) => setMeshyKey(e.target.value)}
              placeholder={ms.api_key_masked || "msy_..."} className="mt-2 bg-white/5 border-white/15 font-mono" data-testid="meshy-key-input" />
            <p className="text-xs text-neutral-500 mt-2">{t("settings.getKeyMeshy")}{ms.api_key_masked && ` ${t("settings.current", { val: ms.api_key_masked })}`}</p>
          </div>
        )}
        <div className="flex gap-2">
          <Button onClick={saveThreed} disabled={savingThreed} className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="save-threed-btn">
            {savingThreed ? <CircleNotch className="animate-spin" /> : t("settings.save3d")}
          </Button>
          {threedProvider === "meshy" && (
            <Button onClick={saveMeshy} disabled={savingMeshy} variant="outline" className="rounded-full border-white/15 text-neutral-300" data-testid="save-meshy-btn">
              {savingMeshy ? <CircleNotch className="animate-spin" /> : t("settings.saveMeshyOnly")}
            </Button>
          )}
        </div>
      </div>

      <div className="border border-white/10 rounded-xl p-6 bg-[#121212] space-y-5 mt-6">
        <div className="flex items-center gap-2"><EnvelopeSimple size={20} className="text-gold" /><h3 className="font-semibold">{t("settings.email")}</h3></div>
        <p className="text-xs text-neutral-400">{t("settings.emailDesc")}</p>

        {/* Recommended: Google OAuth */}
        <div className="border border-gold/25 rounded-lg p-4 bg-gold/[0.04]" data-testid="gmail-oauth-block">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div>
              <div className="font-medium text-sm flex items-center gap-2">{t("settings.connectGoogle")} <span className="text-[10px] bg-gold/20 text-gold px-2 py-0.5 rounded-full">{t("settings.recommended")}</span></div>
              <div className="text-xs text-neutral-400 mt-0.5">{t("settings.oauthDesc")}</div>
            </div>
            {gmailConn?.connected ? (
              <div className="flex items-center gap-2">
                <span className="text-xs text-emerald-400 flex items-center gap-1"><CheckCircle size={15} weight="fill" /> {gmailConn.google_email}</span>
                <Button variant="outline" size="sm" onClick={disconnectGmail} className="rounded-full border-white/15 text-neutral-300" data-testid="gmail-disconnect-btn">{t("settings.disconnect")}</Button>
              </div>
            ) : (
              <Button onClick={connectGmail} disabled={!gmailConn?.configured} className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="gmail-connect-btn">
                <EnvelopeSimple size={16} className="mr-1" /> {t("settings.connectGmail")}
              </Button>
            )}
          </div>
          {gmailConn && !gmailConn.configured && <p className="text-[11px] text-amber-300 mt-2">{t("settings.oauthNotConfigured")}</p>}
        </div>

        <details className="text-xs text-neutral-400">
          <summary className="cursor-pointer text-neutral-300">{t("settings.smtpAlt")}</summary>
          <div className="mt-3 space-y-4">
        <div className="flex items-start gap-2 text-xs text-neutral-400 bg-white/5 rounded-lg p-3">
          <Key size={16} className="text-gold shrink-0 mt-0.5" />
          <span>{t("settings.appPasswordHelp")}</span>
        </div>
        <div>
          <Label>{t("settings.yourGmail")}</Label>
          <Input type="email" value={gmail} onChange={(e) => setGmail(e.target.value)} placeholder="you@gmail.com" className="mt-2 bg-white/5 border-white/15" data-testid="gmail-address-input" />
        </div>
        <div>
          <Label>{t("settings.appPassword")}</Label>
          <Input type="password" value={appPassword} onChange={(e) => setAppPassword(e.target.value)} placeholder={es.app_password_set ? t("settings.appPasswordSet") : "xxxx xxxx xxxx xxxx"} className="mt-2 bg-white/5 border-white/15 font-mono" data-testid="gmail-apppassword-input" />
          {es.app_password_set && <p className="text-xs text-emerald-400 mt-1">{t("settings.appPasswordOk")}</p>}
        </div>
        <Button onClick={saveEmail} disabled={savingEmail} className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="save-email-btn">
          {savingEmail ? <CircleNotch className="animate-spin" /> : t("settings.saveEmail")}
        </Button>
          </div>
        </details>
      </div>

      <div className="border border-white/10 rounded-xl p-6 bg-[#121212] space-y-4 mt-6" data-testid="stripe-connect-card">
        <div className="flex items-center gap-2"><Bank size={20} className="text-gold" /><h3 className="font-semibold">{t("settings.stripeTitle")}</h3></div>
        <p className="text-xs text-neutral-400">{t("settings.stripeDesc")}</p>
        {connect && connect.configured === false ? (
          <div className="text-sm text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg p-3">{t("settings.stripeNotActivated")}</div>
        ) : connect?.connected ? (
          <div className="flex items-center gap-2 text-emerald-400 text-sm" data-testid="connect-status-ok"><CheckCircle weight="fill" /> {t("settings.stripeConnected")}</div>
        ) : (
          <>
            {connect?.account_id && <div className="text-xs text-amber-400">{t("settings.stripePending")}</div>}
            <Button onClick={onboard} disabled={connecting} className="rounded-full bg-gold text-obsidian hover:bg-gold/90 font-semibold" data-testid="connect-onboard-btn">
              {connecting ? <CircleNotch className="animate-spin" /> : (connect?.account_id ? t("settings.stripeContinue") : t("settings.stripeConnect"))}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
